
import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"
import { useTheme } from "@/contexts/ThemeContext"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-input bg-background hover:bg-secondary hover:text-secondary-foreground",
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-secondary hover:text-secondary-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    const { theme } = useTheme();
    const isLightMode = theme === 'light';
    
    // Apply light mode styles for specific button variants with improved contrast
    let lightModeClass = "";
    if (isLightMode) {
      if (variant === "default") {
        lightModeClass = "bg-blue-600 hover:bg-blue-700 text-white";
      } else if (variant === "outline") {
        lightModeClass = "bg-white border-gray-300 hover:bg-gray-50 text-gray-800";
      } else if (variant === "secondary") {
        lightModeClass = "bg-gray-100 hover:bg-gray-200 text-gray-800";
      } else if (variant === "ghost") {
        lightModeClass = "hover:bg-gray-100 text-gray-800";
      } else if (variant === "destructive") {
        lightModeClass = "bg-red-600 hover:bg-red-700 text-white";
      } else if (variant === "link") {
        lightModeClass = "text-blue-600 hover:text-blue-700";
      }
    }
    
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }), lightModeClass)}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
